import { useState, useEffect, useCallback, useRef } from 'react';
import moment from 'moment';
import debounce from 'lodash.debounce';
import { DatePicker, Pagination } from 'rsuite';
import { toast } from 'react-toastify';
import CustomSelect from './helper/CustomSelect';
import makeStyles from '@mui/styles/makeStyles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Grid, Typography, Box, Button, FormLabel } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { LastWeekDates, DateFilter, WeekDates } from './helper/Constant';
import BranchSearchPicker from './helper/BranchSearchPicker';
import RmSearchPicker from './helper/RmSearchPicker';
import { truncateString } from './helper/Utils';
import Heading from 'src/components/Header/Heading';
import {
  fetchResellerDetails,
  fetchRmDetails,
  fetchTATDataReimbursement,
  DownloadTATReport
} from 'src/redux/action/claimService';
import LoadingDetails from 'src/components/common/shimmerLoader/LoadingDetails';

const useStyles = makeStyles((theme) => ({
  table: {
    minWidth: 650
  },
  texthead: {
    fontSize: 12,
    fontWeight: 'bold',
    color: 'white',
    backgroundColor: '#db2228',
    textAlign: 'center',
    border: '0.1px solid rgb(224, 224, 224)',
    padding: '10px 10px'
  },
  textbody: {
    fontSize: 14,
    color: '#172B4D',
    backgroundColor: 'white',
    textAlign: 'center',
    border: '0.1px solid rgb(224, 224, 224)',
    padding: '8px 8px'
  },
  tableContainer: {
    marginTop: 20,
    padding: '0px',
    overflowX: 'auto',
    border: '0.1px solid rgb(224, 224, 224)'
  },
  groupHeader: {
    backgroundColor: '#db2228',
    textAlign: 'center',
    fontWeight: 'bold',
    color: 'white',
    border: '0.1px solid rgb(224, 224, 224)',
    padding: '12px 12px'
  }
}));

const TATSettlementReport = () => {
  const classes = useStyles();
  const prevPayloadRef = useRef(null);
  const scrollRef = useRef();
  const [branchList, setBranchList] = useState([]);
  const [branchInput, setBranchInput] = useState('');
  const [branchLoading, setBranchLoading] = useState(false);
  const [branchPage, setBranchPage] = useState(0);
  const [rmList, setRmList] = useState([]);
  const [rmInput, setRmInput] = useState('');
  const [rmLoading, setRmLoading] = useState(false);
  const [rmPage, setRmPage] = useState(0);

  const initialFilters = {
    duration: 'thisMonth',
    fromDate: WeekDates('month')[0],
    toDate: WeekDates('month')[1],
    resellerId: '',
    rmId: '',
    size: 20,
    page: 0,
    sortedBy: 'asc',
    sortedOn: 'partnerName',
    statuses: ['SETTELED']
  };

  const [filters, setFilters] = useState({ ...initialFilters });
  const [errors, setErrors] = useState({});

  const [tableData, setTableData] = useState([]);
  const [count, setCount] = useState('');
  const [loadingTable, setLoadingTable] = useState(false);
  const [totalTableData, setTotalTableData] = useState([]);

  const getBranches = async (page, reseller_id) => {
    setBranchLoading(true);
    let data = {
      page: page ? page : branchPage,
      size: 20,
      key: branchInput
    };
    if (reseller_id) {
      data.resellerId = reseller_id;
    }
    try {
      const response = await fetchResellerDetails(data);
      if (response?.code === 200) {
        const newArray = [];
        response?.data.forEach((obj) => {
          const exists = branchList.some((item) => item.value === obj.id);
          if (!exists) {
            newArray.push({
              ...obj,
              label: `${obj?.resellerName} (${obj?.mobile})`,
              value: obj?.id
            });
          }
        });
        setBranchList((prev) => [...prev, ...newArray]);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    } finally {
      setBranchLoading(false);
    }
  };

  const getRMDetails = async (page) => {
    setRmLoading(true);
    let data = {
      page: page ? page : rmPage,
      size: 20,
      key: rmInput
    };

    try {
      const response = await fetchRmDetails(data);
      if (response?.code === 200) {
        const newArray = [];
        response?.data.forEach((obj) => {
          const exists = rmList.some((item) => item.value === obj.rmId);
          if (!exists) {
            newArray.push({
              ...obj,
              label: `${obj?.rmName} (${obj?.rmMobile})`,
              value: obj?.rmId
            });
          }
        });
        setRmList((prev) => [...prev, ...newArray]);
      }
    } catch (error) {
      console.error('Error fetching RM details:', error);
    } finally {
      setRmLoading(false);
    }
  };

  const getBranchesScroll = () => {
    getBranches(branchPage + 1);
    setBranchPage(branchPage + 1);
  };

  const getRMScroll = () => {
    getRMDetails(rmPage + 1);
    setRmPage(rmPage + 1);
  };

  const onBranchSearchHandler = (value) => {
    if (value?.length > 2 && /^[a-zA-Z0-9\s_]+$/.test(value))
      setBranchInput(value);
  };

  const onRMSearchHandler = (value) => {
    if (value?.length > 2 && /^[a-zA-Z0-9\s_]+$/.test(value)) setRmInput(value);
  };

  const getBranchDebounceFunction = useCallback(debounce(getBranches, 1000), [
    branchInput
  ]);

  const getRMDebounceFunction = useCallback(debounce(getRMDetails, 1000), [
    rmInput
  ]);

  useEffect(() => {
    if (branchInput) getBranchDebounceFunction();
    return () => getBranchDebounceFunction.cancel();
  }, [branchInput, getBranchDebounceFunction]);

  useEffect(() => {
    if (rmInput) getRMDebounceFunction();
    return () => getRMDebounceFunction.cancel();
  }, [rmInput, getRMDebounceFunction]);

  useEffect(() => {
    getBranches();
    getRMDetails();
    fetchData();
  }, []);

  const handleDateChange = (field, value) => {
    const newFilter = { ...filters, [field]: value };
    setFilters(newFilter);

    const newErrors = { ...errors };

    if (newFilter.duration === 'custom') {
      if (!value || !moment(value, 'DD-MM-YYYY', true).isValid()) {
        newErrors[field] = 'Enter valid date';
      } else {
        delete newErrors[field];
      }

      if (!newFilter.fromDate) {
        newErrors.fromDate = 'Start date is required';
      }
      if (!newFilter.toDate) {
        newErrors.toDate = 'End date is required';
      }
      if (
        newFilter.fromDate &&
        newFilter.toDate &&
        moment(newFilter.fromDate, 'DD-MM-YYYY').isAfter(
          moment(newFilter.toDate, 'DD-MM-YYYY')
        )
      ) {
        newErrors.toDate = 'End date cannot be before start date';
      }
    }

    setErrors(newErrors);
  };

  const handleDurationChange = (value) => {
    let dates;
    if (value === 'custom') {
      setFilters((state) => ({ ...state, duration: value }));
      setErrors({});
      return;
    }

    if (value === 'today') dates = WeekDates('day');
    if (value === 'yesterday') dates = LastWeekDates('day');
    if (value === 'thisWeek') dates = WeekDates('week');
    if (value === 'previousWeek') dates = LastWeekDates('week');
    if (value === 'thisMonth') dates = WeekDates('month');
    if (value === 'previousMonth') dates = LastWeekDates('month');
    if (value === 'thisYear') dates = WeekDates('year');
    if (value === 'previousYear') dates = LastWeekDates('year');

    setFilters((state) => ({
      ...state,
      fromDate: new Date(dates[0]).toISOString().split('T')[0],
      toDate: new Date(dates[1]).toISOString().split('T')[0],
      duration: value
    }));
    setErrors({});
  };

  const formatDate = (date) => {
    if (!date) return '';
    return moment(date).format('YYYY-MM-DD');
  };

  const fetchData = async (data) => {
    let payload = data || filters;

    const formattedPayload = {
      ...payload,
      fromDate: formatDate(payload.fromDate),
      toDate: formatDate(payload.toDate)
    };

    if (
      JSON.stringify(formattedPayload) ===
      JSON.stringify(prevPayloadRef.current)
    ) {
      return;
    }

    setLoadingTable(true);
    try {
      const response = await fetchTATDataReimbursement(formattedPayload);

      if (response?.code === 200) {
        setTableData(response?.data?.data);
        setTotalTableData(response?.data?.total);
        setCount(response?.count);
        prevPayloadRef.current = formattedPayload;
      } else {
        setTableData([]);
        setTotalTableData([]);
        setCount('');
        toast.error(response?.message || 'Something went wrong');
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
      setTableData([]);
      setTotalTableData([]);
      setCount('');
      toast.error('Something went wrong');
    } finally {
      setLoadingTable(false);
    }
  };
  const handleReset = () => {
    const resetFilters = {
      duration: 'thisMonth',
      fromDate: WeekDates('month')[0],
      toDate: WeekDates('month')[1],
      resellerId: '',
      rmId: '',
      size: 20,
      page: 0,
      sortedBy: 'asc',
      sortedOn: 'partnerName'
    };

    setFilters(resetFilters);
    fetchData(resetFilters);

    setErrors({});
  };

  const handleSearch = () => {
    if (filters.duration === 'custom') {
      const newErrors = {};

      if (!filters.fromDate) {
        newErrors.fromDate = 'Start date is required';
      }
      if (!filters.toDate) {
        newErrors.toDate = 'End date is required';
      }
      if (
        filters.fromDate &&
        filters.toDate &&
        filters.fromDate > filters.toDate
      ) {
        newErrors.toDate = 'End date cannot be before start date';
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }
    }

    setErrors({});
    fetchData();
  };

  const handlePageChange = (page) => {
    setFilters((prev) => {
      const data = {
        ...prev,
        page: page - 1
      };
      fetchData(data);
      return data;
    });
  };

  const isSearchDisabled =
    filters.duration === 'custom' &&
    (errors.fromDate || errors.toDate || !filters.fromDate || !filters.toDate);

  const handleDownload = async () => {
    if (!tableData || tableData.length === 0) {
      toast.error('No data available to download');
      return;
    }

    const { page, size, ...restFilters } = filters;

    const formattedFilters = {
      ...restFilters,
      fromDate: formatDate(restFilters.fromDate),
      toDate: formatDate(restFilters.toDate)
    };

    try {
      const response = await DownloadTATReport(formattedFilters);

      const blob = new Blob([response?.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'TatSettlementReport.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('TAT Settlement Report Report Successfully Downloaded');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download TAT Settlement Report');
    }
  };
  const handleSort = (dataKey) => {
    const newSortType =
      filters.sortedOn === dataKey
        ? filters.sortedBy === 'asc'
          ? 'desc'
          : 'asc'
        : 'asc';

    const newFilters = {
      ...filters,
      sortedOn: dataKey,
      sortedBy: newSortType,
      page: 0
    };

    setFilters(newFilters);
    fetchData(newFilters);
  };

  return (
    <div style={{ padding: 5 }}>
      <Heading title={'Settlement TAT Report'} />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={2.5}>
          <label
            style={{
              display: 'block',
              marginBottom: '5px',
              color: 'rgb(107, 114, 128)',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            Date Filter <span style={{ color: '#EF4444' }}>*</span>
          </label>
          <CustomSelect
            label=""
            value={filters.duration}
            onChange={(e) => handleDurationChange(e.target.value)}
            data={DateFilter}
          />
        </Grid>

        {filters.duration === 'custom' && (
          <>
            <Grid item xs={12} sm={6} md={2.5}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '5px',
                  color: 'rgb(107, 114, 128)',
                  fontSize: '12px',
                  fontWeight: '500'
                }}
              >
                From Date <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <div className="relative" ref={scrollRef}>
                <DatePicker
                  container={() => scrollRef.current}
                  format="dd-MM-yyyy"
                  value={filters.fromDate ? new Date(filters.fromDate) : null}
                  shouldDisableDate={(date) => moment(date).isAfter(moment())}
                  onChange={(e) => handleDateChange('fromDate', e)}
                  cleanable={false}
                  oneTap
                  style={{
                    border: errors.fromDate ? '1px solid #db2228' : '',
                    borderRadius: '6px',
                    width: '100%'
                  }}
                />
              </div>
              {errors.fromDate && (
                <Typography
                  style={{
                    color: '#d32f2f',
                    fontSize: '0.75rem',
                    marginTop: '4px',
                    lineHeight: '1.2',
                    fontWeight: 400
                  }}
                >
                  {errors.fromDate}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '5px',
                  color: 'rgb(107, 114, 128)',
                  fontSize: '12px',
                  fontWeight: '500'
                }}
              >
                To Date <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <div className="relative" ref={scrollRef}>
                <DatePicker
                  container={() => scrollRef.current}
                  format="dd-MM-yyyy"
                  value={filters.toDate ? new Date(filters.toDate) : null}
                  shouldDisableDate={(date) => moment(date).isAfter(moment())}
                  onChange={(e) => handleDateChange('toDate', e)}
                  cleanable={false}
                  oneTap
                  style={{
                    border: errors.toDate ? '1px solid #db2228' : '',
                    borderRadius: '6px',
                    width: '100%'
                  }}
                />
              </div>
              {errors.toDate && (
                <Typography
                  style={{
                    color: '#d32f2f',
                    fontSize: '0.75rem',
                    marginTop: '4px',
                    lineHeight: '1.2',
                    fontWeight: 400
                  }}
                >
                  {errors.toDate}
                </Typography>
              )}
            </Grid>
          </>
        )}

        <Grid item xs={12} sm={6} md={2.5}>
          <label
            style={{
              display: 'block',
              marginBottom: '5px',
              color: 'rgb(107, 114, 128)',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            Sales RM
          </label>
          <RmSearchPicker
            data={rmList}
            name="rmId"
            value={filters?.rmId}
            onChange={(value) => {
              setFilters((prev) => ({
                ...prev,
                rmId: value
              }));
            }}
            request={getRMScroll}
            onSearch={onRMSearchHandler}
            loading={rmLoading}
            cleanable={true}
            width="100%"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.5}>
          <label
            style={{
              display: 'block',
              marginBottom: '5px',
              color: 'rgb(107, 114, 128)',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            OP Cash Reseller
          </label>
          <BranchSearchPicker
            data={branchList}
            name="resellerId"
            value={filters?.resellerId}
            onChange={(value) => {
              setFilters((prev) => ({
                ...prev,
                resellerId: value
              }));
            }}
            request={getBranchesScroll}
            onSearch={onBranchSearchHandler}
            loading={branchLoading}
            cleanable={true}
            width="100%"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box
            sx={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
              height: '100%',
              width: '100%'
            }}
          >
            <div>
              <FormLabel
                sx={{
                  display: { xs: 'none', lg: 'block' },
                  marginBottom: '5px',
                  color: 'rgb(107, 114, 128)',
                  fontSize: '12px',
                  fontWeight: '500',
                  opacity: '0'
                }}
              >
                Search
              </FormLabel>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                disabled={isSearchDisabled}
                size="small"
                sx={{
                  flex: { xs: 1, sm: 'none' },
                  '&.Mui-disabled': {
                    cursor: 'not-allowed',
                    pointerEvents: 'auto',
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)'
                  }
                }}
              >
                Search
              </Button>
              {(errors.fromDate || errors.toDate) && (
                <Typography
                  sx={{
                    display: { xs: 'none', lg: 'block' },
                    lineHeight: '1.2',
                    opacity: '0'
                  }}
                >
                  error
                </Typography>
              )}
            </div>
            <div>
              <FormLabel
                sx={{
                  display: { xs: 'none', lg: 'block' },
                  marginBottom: '5px',
                  color: 'rgb(107, 114, 128)',
                  fontSize: '12px',
                  fontWeight: '500',
                  opacity: '0'
                }}
              >
                Reset
              </FormLabel>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleReset}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Reset
              </Button>
              {(errors.fromDate || errors.toDate) && (
                <Typography
                  sx={{
                    display: { xs: 'none', lg: 'block' },
                    lineHeight: '1.2',
                    opacity: '0'
                  }}
                >
                  error
                </Typography>
              )}
            </div>
            <div>
              <FormLabel
                sx={{
                  display: { xs: 'none', lg: 'block' },
                  marginBottom: '5px',
                  color: 'rgb(107, 114, 128)',
                  fontSize: '12px',
                  fontWeight: '500',
                  opacity: '0'
                }}
              >
                Download
              </FormLabel>
              <Button
                variant="contained"
                color="primary"
                size="small"
                sx={{
                  minWidth: '40px',
                  px: 0,
                  '&.Mui-disabled': {
                    cursor: 'not-allowed !important',
                    pointerEvents: 'auto'
                  }
                }}
                disabled={
                  !!errors.fromDate ||
                  !!errors.toDate ||
                  !tableData ||
                  tableData?.length === 0
                }
              >
                <FileDownloadOutlinedIcon
                  fontSize="small"
                  onClick={handleDownload}
                />
              </Button>
              {(errors.fromDate || errors.toDate) && (
                <Typography
                  sx={{
                    display: { xs: 'none', lg: 'block' },
                    lineHeight: '1.2',
                    opacity: '0'
                  }}
                >
                  error
                </Typography>
              )}
            </div>
          </Box>
        </Grid>
      </Grid>

      {loadingTable ? (
        <LoadingDetails num={6} hideImg={true} />
      ) : (
        <>
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table
              className={classes.table}
              aria-label="tat settlement report table"
              stickyHeader
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    className={classes.groupHeader}
                    colSpan={2}
                    style={{ minWidth: 480 }}
                  >
                    Settlement TAT Report
                  </TableCell>

                  <TableCell
                    className={classes.groupHeader}
                    colSpan={6}
                    style={{ minWidth: 480 }}
                  >
                    TAT Bucket
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    className={classes.texthead}
                    style={{ minWidth: 200, textAlign: 'left' }}
                    rowSpan={2}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'left',
                        gap: '4px'
                      }}
                    >
                      <span style={{ fontWeight: 500, fontSize: '12px' }}>
                        Reseller Name
                      </span>
                      {tableData && tableData?.length > 0 && (
                        <span
                          style={{ paddingTop: '3px', cursor: 'pointer' }}
                          onClick={() => handleSort('partnerName')}
                        >
                          {filters.sortedOn === 'partnerName' ? (
                            filters.sortedBy === 'desc' ? (
                              <ArrowDownwardIcon fontSize="inherit" />
                            ) : (
                              <ArrowUpwardIcon fontSize="inherit" />
                            )
                          ) : null}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell
                    className={classes.texthead}
                    style={{ minWidth: 180, textAlign: 'left' }}
                    rowSpan={2}
                  >
                    Sales RM
                  </TableCell>
                  <TableCell
                    className={classes.texthead}
                    style={{ minWidth: 80 }}
                  >
                    00 to 07 Days
                  </TableCell>
                  <TableCell
                    className={classes.texthead}
                    style={{ minWidth: 80 }}
                  >
                    08 to 14 Days
                  </TableCell>
                  <TableCell
                    className={classes.texthead}
                    style={{ minWidth: 80 }}
                  >
                    15 to 28 Days
                  </TableCell>
                  <TableCell
                    className={classes.texthead}
                    style={{ minWidth: 80 }}
                  >
                    29 to 45 Days
                  </TableCell>
                  <TableCell
                    className={classes.texthead}
                    style={{ minWidth: 80 }}
                  >
                    45+ Days
                  </TableCell>
                  <TableCell
                    className={classes.texthead}
                    style={{ minWidth: 80 }}
                  >
                    Grand Total
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {tableData && tableData?.length > 0 ? (
                  <>
                    <TableRow key="total" hover>
                      <TableCell
                        className={classes.textbody}
                        style={{
                          textAlign: 'left',
                          minWidth: 190,
                          fontWeight: 'bold'
                        }}
                      ></TableCell>
                      <TableCell
                        className={classes.textbody}
                        style={{
                          textAlign: 'left',
                          minWidth: 190,
                          fontWeight: 'bold'
                        }}
                      >
                        Total
                      </TableCell>
                      <TableCell
                        className={classes.textbody}
                        style={{ minWidth: 80, fontWeight: 'bold' }}
                      >
                        {totalTableData?.total00to07Days || 0}
                      </TableCell>
                      <TableCell
                        className={classes.textbody}
                        style={{ minWidth: 80, fontWeight: 'bold' }}
                      >
                        {totalTableData?.total08to14Days || 0}
                      </TableCell>
                      <TableCell
                        className={classes.textbody}
                        style={{ minWidth: 80, fontWeight: 'bold' }}
                      >
                        {totalTableData?.total15to28Days || 0}
                      </TableCell>
                      <TableCell
                        className={classes.textbody}
                        style={{ minWidth: 80, fontWeight: 'bold' }}
                      >
                        {totalTableData?.total29to45Days || 0}
                      </TableCell>
                      <TableCell
                        className={classes.textbody}
                        style={{ minWidth: 80, fontWeight: 'bold' }}
                      >
                        {totalTableData?.total45PlusDays || 0}
                      </TableCell>
                      <TableCell
                        className={classes.textbody}
                        style={{ minWidth: 80, fontWeight: 'bold' }}
                      >
                        {(totalTableData?.total00to07Days || 0) +
                          (totalTableData?.total08to14Days || 0) +
                          (totalTableData?.total15to28Days || 0) +
                          (totalTableData?.total29to45Days || 0) +
                          (totalTableData?.total45PlusDays || 0)}
                      </TableCell>
                    </TableRow>
                    {tableData?.map((row, index) => (
                      <TableRow key={index} hover>
                        <TableCell
                          className={classes.textbody}
                          style={{ textAlign: 'left' }}
                        >
                          {truncateString(row?.partner?.toLowerCase(), 25)}
                        </TableCell>
                        <TableCell
                          className={classes.textbody}
                          style={{ textAlign: 'left' }}
                        >
                          {truncateString(row?.salesRM?.toLowerCase(), 25)}
                        </TableCell>
                        <TableCell className={classes.textbody}>
                          {row?.tatBuckets?.['00 to 07 Days'] || 0}
                        </TableCell>
                        <TableCell className={classes.textbody}>
                          {row?.tatBuckets?.['08 to 14 Days'] || 0}
                        </TableCell>
                        <TableCell className={classes.textbody}>
                          {row?.tatBuckets?.['15 to 28 Days'] || 0}
                        </TableCell>
                        <TableCell className={classes.textbody}>
                          {row?.tatBuckets?.['29 to 45 Days'] || 0}
                        </TableCell>
                        <TableCell className={classes.textbody}>
                          {row?.tatBuckets?.['45+ Days'] || 0}
                        </TableCell>
                        <TableCell className={classes.textbody}>
                          {(row?.tatBuckets?.['00 to 07 Days'] || 0) +
                            (row?.tatBuckets?.['08 to 14 Days'] || 0) +
                            (row?.tatBuckets?.['15 to 28 Days'] || 0) +
                            (row?.tatBuckets?.['29 to 45 Days'] || 0) +
                            (row?.tatBuckets?.['45+ Days'] || 0)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                ) : (
                  <TableRow style={{ height: '170px' }}>
                    <TableCell colSpan={7} align="center">
                      No Data Found!
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {tableData && tableData?.length > 0 && (
            <div style={{ padding: 20 }}>
              <Pagination
                prev
                next
                first
                last
                ellipsis
                boundaryLinks
                maxButtons={4}
                size="xs"
                layout={['total', '-', 'pager', 'skip']}
                total={count}
                limit={filters?.size}
                activePage={filters?.page + 1}
                onChangePage={handlePageChange}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TATSettlementReport;
